/**
 * Design System Tokens
 * Defines colors, typography, and spacing for all platforms
 */

/**
 * Color tokens
 */
const colors = {
  // Brand colors
  primary: '#FF6B6B',
  primaryLight: '#FFE0E0',
  primaryDark: '#CC5555',

  secondary: '#FFFFFF',
  secondaryDark: '#F5F5F5',

  // Semantic colors
  success: '#4CAF50',
  successLight: '#E8F5E9',
  warning: '#FFC107',
  warningLight: '#FFF8E1',
  error: '#F44336',
  errorLight: '#FFEBEE',
  info: '#2196F3',
  infoLight: '#E3F2FD',

  // Neutral colors
  black: '#000000',
  white: '#FFFFFF',
  gray1: '#F8F9FA',
  gray2: '#E9ECEF',
  gray3: '#DEE2E6',
  gray4: '#CED4DA',
  gray5: '#ADB5BD',
  gray6: '#6C757D',
  gray7: '#495057',
  gray8: '#343A40',
  gray9: '#212529',

  // Transparent
  transparent: 'transparent',
};

/**
 * Size tokens for spacing
 */
const sizes = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  '3xl': 48,
  '4xl': 64,
  '5xl': 80,
  '6xl': 96,
};

/**
 * Typography tokens
 */
const fonts = {
  body: 'System',
  heading: 'System',
  mono: 'Courier',
};

const fontSizes = {
  xs: 11,
  sm: 13,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
};

const fontWeights = {
  light: '300',
  normal: '400',
  semibold: '600',
  bold: '700',
  extrabold: '800',
};

const lineHeights = {
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.75,
  loose: 2,
};

/**
 * Token configuration object
 */
const tokens = {
  colors,
  space: sizes,
  size: sizes,
  radius: {
    xs: 2,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    '2xl': 16,
    full: 9999,
  },
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },
};

/**
 * Create Tamagui theme
 */
const theme = {
  // Light theme
  light: {
    bg: colors.white,
    bgSecondary: colors.gray1,
    bgTertiary: colors.gray2,

    text: colors.gray9,
    textSecondary: colors.gray6,
    textTertiary: colors.gray5,

    border: colors.gray3,
    borderSecondary: colors.gray4,

    primary: colors.primary,
    primaryText: colors.white,

    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
  },

  // Dark theme (optional)
  dark: {
    bg: colors.gray9,
    bgSecondary: colors.gray8,
    bgTertiary: colors.gray7,

    text: colors.white,
    textSecondary: colors.gray5,
    textTertiary: colors.gray4,

    border: colors.gray7,
    borderSecondary: colors.gray6,

    primary: colors.primary,
    primaryText: colors.white,

    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
  },
};

/**
 * Export configuration
 */
export { colors, sizes, fonts, fontSizes, fontWeights, lineHeights, theme, tokens };

